import React, { useState, useEffect, useCallback } from 'react';
import { getProcessedClients, resetProcessedClient, getClaimsPool, deleteClaimsPoolRecord, runClaimsPoolJob, rerunClaims } from '../services/api';

export default function ProcessedClients() {
	const [activeTab, setActiveTab] = useState('processed');
	const [clients, setClients] = useState([]);
	const [poolRecords, setPoolRecords] = useState([]);
	const [loading, setLoading] = useState(true);
	const [poolLoading, setPoolLoading] = useState(true);
	const [error, setError] = useState('');
	const [resetting, setResetting] = useState(null);
	const [deleting, setDeleting] = useState(null);
	const [runningJob, setRunningJob] = useState(false);
	const [jobMessage, setJobMessage] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [rerunning, setRerunning] = useState(null);
	const [rerunResult, setRerunResult] = useState(null);

	const fetchClients = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const data = await getProcessedClients();
			setClients(data.clients || []);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, []);

	const fetchPool = useCallback(async () => {
		setPoolLoading(true);
		try {
			const data = await getClaimsPool();
			setPoolRecords(data.records || []);
		} catch (err) {
			// Pool table might not exist yet
			setPoolRecords([]);
		} finally {
			setPoolLoading(false);
		}
	}, []);

	useEffect(() => { fetchClients(); fetchPool(); }, [fetchClients, fetchPool]);

	const handleReset = async (crmRecordId) => {
		if (!window.confirm(`Reset client ${crmRecordId} for reprocessing?\n\nThis will allow the credit check to run again for this client.`)) return;
		setResetting(crmRecordId);
		try {
			await resetProcessedClient(crmRecordId);
			await fetchClients();
		} catch (err) {
			setError(`Failed to reset: ${err.message}`);
		} finally {
			setResetting(null);
		}
	};

	const handleDeletePool = async (rowId) => {
		if (!window.confirm('Delete this pooled record? This cannot be undone.')) return;
		setDeleting(rowId);
		try {
			await deleteClaimsPoolRecord(rowId);
			await fetchPool();
		} catch (err) {
			setError(`Failed to delete: ${err.message}`);
		} finally {
			setDeleting(null);
		}
	};

	const handleRunJob = async () => {
		setRunningJob(true);
		setJobMessage('');
		setError('');
		try {
			const result = await runClaimsPoolJob();
			setJobMessage(result.message || 'Job triggered successfully.');
			// Refresh pool after a short delay to show updated statuses
			setTimeout(() => { fetchPool(); setJobMessage(''); }, 5000);
		} catch (err) {
			setError(`Failed to trigger job: ${err.message}`);
		} finally {
			setRunningJob(false);
		}
	};

	const handleRerunClaims = async (crmRecordId) => {
		if (!window.confirm(`Re-run claims for ${crmRecordId}?\n\nThis will read the existing Equifax JSON from WorkDrive and create any missing claims in CRM.`)) return;
		setRerunning(crmRecordId);
		setRerunResult(null);
		setError('');
		try {
			const result = await rerunClaims(crmRecordId);
			setRerunResult({ crmRecordId, ...result.claimsSummary });
			await fetchClients();
		} catch (err) {
			setError(`Re-run failed: ${err.message}`);
		} finally {
			setRerunning(null);
		}
	};

	const filtered = clients.filter(c =>
		!searchTerm ||
		c.crmRecordId.includes(searchTerm) ||
		(c.email || '').toLowerCase().includes(searchTerm.toLowerCase())
	);

	const filteredPool = poolRecords.filter(r =>
		!searchTerm ||
		(r.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
		(r.leadId || '').includes(searchTerm)
	);

	const pendingCount = poolRecords.filter(r => r.status === 'pending' || r.status === 'failed').length;

	return (
		<div className="processed-clients-container">
			{error && <div className="message error">{error}</div>}

			{/* Tabs */}
			<div className="tabs" style={{ marginBottom: '1rem' }}>
				<button
					className={`tab ${activeTab === 'processed' ? 'active' : ''}`}
					onClick={() => setActiveTab('processed')}
				>
					Processed Clients ({clients.length})
				</button>
				<button
					className={`tab ${activeTab === 'pool' ? 'active' : ''}`}
					onClick={() => setActiveTab('pool')}
				>
					Claims Pool {pendingCount > 0 && <span className="status-badge blocked" style={{ marginLeft: '6px', fontSize: '0.7rem' }}>{pendingCount}</span>}
				</button>
			</div>

			<div className="processed-clients-toolbar">
				<div className="processed-clients-search">
					<input
						type="text"
						placeholder={activeTab === 'processed' ? 'Search by CRM ID or email...' : 'Search by email or Lead ID...'}
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
					/>
					<span className="processed-clients-count">
						{activeTab === 'processed'
							? `${filtered.length} of ${clients.length} client${clients.length !== 1 ? 's' : ''}`
							: `${filteredPool.length} of ${poolRecords.length} record${poolRecords.length !== 1 ? 's' : ''}`
						}
					</span>
				</div>
				<button onClick={() => { fetchClients(); fetchPool(); }} className="btn-secondary processed-clients-refresh">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
					Refresh
				</button>
			</div>

			{/* Processed Clients Tab */}
			{activeTab === 'processed' && (
				<>
					{loading ? (
						<div className="processed-clients-loading">Loading processed clients...</div>
					) : filtered.length === 0 ? (
						<div className="processed-clients-empty">
							{clients.length === 0 ? 'No processed clients found.' : 'No clients match your search.'}
						</div>
					) : (
						<div className="processed-clients-table-wrap">
							<table className="logs-table">
								<thead>
									<tr>
										<th>CRM Record ID</th>
										<th>Email</th>
										<th>Processed Date</th>
										<th>Status</th>
										<th className="processed-clients-action-cell">Action</th>
									</tr>
								</thead>
								<tbody>
									{filtered.map(c => (
										<tr key={c.crmRecordId} className="log-row">
											<td className="processed-clients-crm-id">{c.crmRecordId}</td>
											<td>{c.email || <span className="processed-clients-muted">-</span>}</td>
											<td>{c.processedAt ? new Date(c.processedAt).toLocaleString() : '-'}</td>
											<td><span className="status-badge success">{c.processedStatus}</span></td>
											<td className="processed-clients-action-cell" style={{ display: 'flex', gap: '0.4rem' }}>
												<button
													onClick={() => handleRerunClaims(c.crmRecordId)}
													disabled={rerunning === c.crmRecordId}
													className="btn-primary"
													style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
													title="Re-run claims from existing Equifax JSON without re-running Kount/Equifax"
												>
													{rerunning === c.crmRecordId ? 'Running...' : 'Re-run Claims'}
												</button>
												<button
													onClick={() => handleReset(c.crmRecordId)}
													disabled={resetting === c.crmRecordId}
													className="btn-reset"
												>
													{resetting === c.crmRecordId ? 'Resetting...' : 'Reset'}
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}

					{rerunResult && (
						<div className="message success" style={{ marginTop: '1rem' }}>
							<strong>Re-run complete for {rerunResult.crmRecordId}:</strong>{' '}
							{rerunResult.created} created, {rerunResult.skipped} skipped, {rerunResult.flagged} flagged, {rerunResult.failed} failed
							<button onClick={() => setRerunResult(null)} style={{ marginLeft: '1rem', fontSize: '0.8rem' }} className="btn-secondary">Dismiss</button>
						</div>
					)}
				</>
			)}

			{/* Claims Pool Tab */}
			{activeTab === 'pool' && (
				<>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
						<p className="form-hint" style={{ margin: 0 }}>
							Claims pooled from Lead records that haven't been converted to Contacts yet. The scheduled job processes these automatically every hour.
						</p>
						<button
							onClick={handleRunJob}
							disabled={runningJob || pendingCount === 0}
							className="btn-primary"
							style={{ whiteSpace: 'nowrap', marginLeft: '1rem', opacity: pendingCount === 0 ? 0.5 : 1 }}
							title={pendingCount === 0 ? 'No pending records to process' : `Process ${pendingCount} pending record(s)`}
						>
							{runningJob ? (
								<>
									<svg className="btn-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
									Running...
								</>
							) : (
								<>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
									Run Now
								</>
							)}
						</button>
					</div>
					{jobMessage && <div className="message success" style={{ marginBottom: '0.75rem' }}>{jobMessage}</div>}
					{poolLoading ? (
						<div className="processed-clients-loading">Loading claims pool...</div>
					) : filteredPool.length === 0 ? (
						<div className="processed-clients-empty">
							{poolRecords.length === 0 ? 'No pooled claims. All clear.' : 'No records match your search.'}
						</div>
					) : (
						<div className="processed-clients-table-wrap">
							<table className="logs-table">
								<thead>
									<tr>
										<th>Email</th>
										<th>Lead ID</th>
										<th>Status</th>
										<th>Attempts</th>
										<th>Created</th>
										<th>Last Attempt</th>
										<th>Error</th>
										<th className="processed-clients-action-cell">Action</th>
									</tr>
								</thead>
								<tbody>
									{filteredPool.map(r => (
										<tr key={r.ROWID} className="log-row">
											<td>{r.email || '-'}</td>
											<td className="processed-clients-crm-id">{r.leadId || '-'}</td>
											<td>
												<span className={`status-badge ${r.status === 'pending' ? 'blocked' : r.status === 'failed' ? 'error' : r.status === 'processing' ? 'blocked' : 'success'}`}>
													{r.status}
												</span>
											</td>
											<td>{r.attempts || '0'}</td>
											<td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
											<td>{r.lastAttemptAt ? new Date(r.lastAttemptAt).toLocaleString() : '-'}</td>
											<td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.errorMessage || ''}>
												{r.errorMessage || '-'}
											</td>
											<td className="processed-clients-action-cell">
												<button
													onClick={() => handleDeletePool(r.ROWID)}
													disabled={deleting === r.ROWID}
													className="btn-reset"
												>
													{deleting === r.ROWID ? 'Deleting...' : 'Delete'}
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</>
			)}
		</div>
	);
}
